import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Platform,
  Alert,
} from 'react-native';

import { AttachmentActions } from './AttachmentActions';
import { AttachmentThumbnail } from './AttachmentThumbnail';
import { ATTACHMENT_COLORS } from '../../constants/attachmentColors';
import { attachmentService } from '../../services/attachmentService';
import {
  isImageMimeType,
  isVideoMimeType,
  isAudioMimeType,
} from '../../services/mediaViewerService';
import { networkService } from '../../services/network';
import { type AttachmentListItemProps } from '../../types/attachmentInterfaces';
import { isCarouselSupported } from '../../utils/attachmentCarousel';
import {
  formatFileSize,
  formatDate,
  formatFileName,
} from '../../utils/attachmentFormatters';
import { isPDFType } from '../../utils/attachmentHelpers';
import { showToast } from '../../utils/toast';

const CAPTION_PLACEHOLDER = 'Add caption...';

/**
 * AttachmentListItem component
 * Displays a single attachment with consistent styling across all views.
 * Supports both desktop table layout and mobile card layout.
 */
export const AttachmentListItem: React.FC<AttachmentListItemProps> = ({
  attachment,
  layout,
  isEditing,
  isMobile,
  onCaptionChange,
  onDelete,
  pendingCaptionEdits,
  error,
  onRetry,
  onOpenCarousel,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const isPDF = isPDFType(attachment.mimeType);
  const isImage = isImageMimeType(attachment.mimeType || '');
  const isVideo = isVideoMimeType(attachment.mimeType || '');
  const isAudio = isAudioMimeType(attachment.mimeType || '');
  const isText = attachment.mimeType === 'text/plain';
  const isCarousel = isCarouselSupported(attachment.mimeType);
  const isViewable = isImage || isVideo || isAudio || isPDF || isText;

  const captionValue =
    pendingCaptionEdits?.[attachment.id] ?? attachment.captions ?? '';

  const handleView = () => {
    if (isCarousel && onOpenCarousel) {
      onOpenCarousel(attachment);
    }
  };

  const handleDownload = () =>
    attachmentService.downloadAttachment(attachment.id);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await attachmentService.deleteAttachment(attachment.id);
      if (result.success) {
        onDelete(attachment.id);
      } else {
        throw new Error(result.error || 'Failed to delete attachment');
      }
    } catch (error) {
      setIsDeleting(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to delete attachment';

      if (Platform.OS === 'web') {
        alert(`Delete failed: ${errorMessage}`);
      } else {
        Alert.alert('Delete Failed', errorMessage);
      }
    }
  };

  /**
   * Handle caption changes with validation and offline support.
   *
   * Offline Queue Behavior:
   * - When online: Updates are sent immediately to the server
   * - When offline: Updates are queued in offlineCache for later sync
   * - Queue is processed automatically when network connection is restored
   * - Queued operations are persisted in AsyncStorage (survive app restart)
   * - If queue is full or fails, user is notified via toast message
   * - Maximum queue size: 100 operations (configured in offlineCache)
   *
   * @param newCaption - The new caption text (max 500 characters)
   */
  const handleCaptionChange = async (newCaption: string) => {
    if (onCaptionChange) {
      // Validate caption length (database field limit)
      const MAX_CAPTION_LENGTH = 500;
      if (newCaption.length > MAX_CAPTION_LENGTH) {
        const errorMsg = `Caption too long (max ${MAX_CAPTION_LENGTH} characters)`;
        if (Platform.OS === 'web') {
          alert(errorMsg);
        } else {
          Alert.alert('Caption Too Long', errorMsg);
        }
        return;
      }

      // Update local state immediately for responsive UX
      onCaptionChange(attachment.id, newCaption);

      // If offline, notify user that caption changes will not be synced
      const isOnline = networkService.isConnected();
      if (!isOnline) {
        showToast(
          'Failed to save caption for offline sync. Changes may be lost.',
        );
      }
    }
  };

  const renderCaptionRow = () => {
    if (isEditing && onCaptionChange) {
      return (
        <TextInput
          value={captionValue}
          onChangeText={handleCaptionChange}
          placeholder={CAPTION_PLACEHOLDER}
          style={
            layout === 'card'
              ? styles.cardCaptionInput
              : styles.tableCaptionInput
          }
          numberOfLines={1}
          editable={!isDeleting}
          underlineColorAndroid="transparent"
        />
      );
    }

    if (captionValue) {
      return (
        <Text
          style={layout === 'card' ? styles.cardCaption : styles.tableCaption}
          numberOfLines={1}
          ellipsizeMode="tail"
        >
          {captionValue}
        </Text>
      );
    }

    return (
      <Text
        style={
          layout === 'card'
            ? styles.cardCaptionPlaceholder
            : styles.tableCaptionPlaceholder
        }
        numberOfLines={1}
        ellipsizeMode="tail"
      >
        {CAPTION_PLACEHOLDER}
      </Text>
    );
  };

  const handleFilenameTap = () => {
    if (isMobile && attachment.attachmentName.length > 30) {
      Alert.alert('Filename', attachment.attachmentName);
    }
  };

  const displayFilename = formatFileName(
    attachment.attachmentName,
    isMobile ? 30 : 50,
  );

  if (layout === 'card') {
    // Mobile card layout
    return (
      <>
        <View
          style={[styles.card, isDeleting && styles.deletingCard]}
          testID="attachment-item"
        >
          <View style={styles.cardRow}>
            {/* Thumbnail + filename — tap to open viewer for viewable files */}
            <TouchableOpacity
              style={styles.cardPreviewArea}
              onPress={isViewable ? handleView : handleFilenameTap}
              disabled={!isViewable && !isMobile}
              accessible={isViewable}
              accessibilityRole={isViewable ? 'button' : undefined}
              accessibilityLabel={
                isViewable ? `View ${attachment.attachmentName}` : undefined
              }
              testID="attachment-preview-tap"
            >
              <AttachmentThumbnail
                attachment={attachment}
                size="xsmall"
                loading={isDeleting}
                onPress={isViewable ? handleView : undefined}
              />
              <View style={styles.cardContent}>
                <Text
                  style={styles.cardFilename}
                  numberOfLines={1}
                  ellipsizeMode="tail"
                >
                  {displayFilename}
                </Text>

                {renderCaptionRow()}

                <Text style={styles.cardMetadata}>
                  {formatDate(attachment.createdAt)} •{' '}
                  {formatFileSize(attachment.fileSizeBytes)}
                </Text>

                {error && (
                  <View style={styles.errorContainer}>
                    <Text style={styles.errorText}>{error}</Text>
                    {onRetry && (
                      <TouchableOpacity onPress={onRetry}>
                        <Text style={styles.retryText}>Retry</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}
              </View>
            </TouchableOpacity>

            <View style={styles.cardActions}>
              <AttachmentActions
                attachment={attachment}
                isEditing={isEditing}
                isDeleting={isDeleting}
                onDownload={handleDownload}
                onDelete={handleDelete}
                layout="vertical"
                disabled={isDeleting}
              />
            </View>
          </View>
        </View>
      </>
    );
  }

  // Desktop table row layout
  return (
    <>
      <View
        style={[styles.tableRow, isDeleting && styles.deletingRow]}
        testID="attachment-item"
      >
        {/* Name column with thumbnail — tap to open viewer when not editing */}
        {isViewable && !isEditing ? (
          <TouchableOpacity
            style={styles.nameColumn}
            onPress={handleView}
            accessible={true}
            accessibilityRole="button"
            accessibilityLabel={`View ${attachment.attachmentName}`}
            testID="attachment-preview-tap"
          >
            <AttachmentThumbnail
              attachment={attachment}
              size="large"
              loading={isDeleting}
            />
            <View style={styles.nameContent}>
              <Text
                style={styles.tableFilename}
                numberOfLines={1}
                ellipsizeMode="tail"
                title={
                  Platform.OS === 'web' ? attachment.attachmentName : undefined
                }
              >
                {displayFilename}
              </Text>

              {renderCaptionRow()}

              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                  {onRetry && (
                    <TouchableOpacity onPress={onRetry}>
                      <Text style={styles.retryText}>Retry</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          </TouchableOpacity>
        ) : (
          <View style={styles.nameColumn}>
            <AttachmentThumbnail
              attachment={attachment}
              size="large"
              loading={isDeleting}
              onPress={isViewable ? handleView : undefined}
            />
            <View style={styles.nameContent}>
              <Text
                style={styles.tableFilename}
                numberOfLines={1}
                ellipsizeMode="tail"
                title={
                  Platform.OS === 'web' ? attachment.attachmentName : undefined
                }
              >
                {displayFilename}
              </Text>

              {renderCaptionRow()}

              {error && (
                <View style={styles.errorContainer}>
                  <Text style={styles.errorText}>{error}</Text>
                  {onRetry && (
                    <TouchableOpacity onPress={onRetry}>
                      <Text style={styles.retryText}>Retry</Text>
                    </TouchableOpacity>
                  )}
                </View>
              )}
            </View>
          </View>
        )}

        {/* Date column */}
        <View style={styles.dateColumn}>
          <Text style={styles.tableText}>
            {formatDate(attachment.createdAt)}
          </Text>
        </View>

        {/* Size column */}
        <View style={styles.sizeColumn}>
          <Text style={styles.tableText}>
            {formatFileSize(attachment.fileSizeBytes)}
          </Text>
        </View>

        {/* Actions column */}
        <View style={styles.actionsColumn}>
          <AttachmentActions
            attachment={attachment}
            isEditing={isEditing}
            isDeleting={isDeleting}
            onDownload={handleDownload}
            onDelete={handleDelete}
            layout="icons-only"
            disabled={isDeleting}
          />
        </View>
      </View>
    </>
  );
};

// Shared box-model bases — single source of truth for caption row height consistency
const CARD_CAPTION_BASE = {
  minHeight: 28,
  paddingVertical: 4,
  marginBottom: 2,
} as const;
const TABLE_CAPTION_BASE = { minHeight: 26, paddingVertical: 2 } as const;

const styles = StyleSheet.create({
  // Card layout styles (mobile)
  card: {
    borderWidth: 1,
    borderColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
    borderRadius: 8,
    padding: 8,
    marginBottom: 8,
    backgroundColor: ATTACHMENT_COLORS.CARD_BACKGROUND_COLOR,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 1 },
        shadowOpacity: 0.1,
        shadowRadius: 2,
      },
      android: {
        elevation: 2,
      },
    }),
  },
  deletingCard: {
    opacity: ATTACHMENT_COLORS.DELETING_OPACITY,
  },
  cardRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  // Thumbnail + filename — tap to open viewer (viewable files) or expand long filename (mobile)
  cardPreviewArea: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  cardContent: {
    flex: 1,
    marginLeft: 8,
    justifyContent: 'flex-start',
  },
  cardFilename: {
    fontSize: 15,
    fontWeight: '500',
    color: ATTACHMENT_COLORS.FILENAME_COLOR,
    marginBottom: 2,
  },
  // Card caption variants share identical box model for consistent card height
  cardCaption: {
    ...CARD_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.CAPTION_COLOR,
  },
  cardCaptionInput: {
    ...CARD_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.CAPTION_COLOR,
  },
  cardCaptionPlaceholder: {
    ...CARD_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.METADATA_COLOR,
  },
  cardMetadata: {
    fontSize: 11,
    color: ATTACHMENT_COLORS.METADATA_COLOR,
    marginTop: 2,
  },
  cardActions: {
    marginLeft: 4,
    alignSelf: 'stretch',
  },

  // Table layout styles (desktop)
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: ATTACHMENT_COLORS.CARD_BORDER_COLOR,
    paddingVertical: 12,
    paddingHorizontal: 8,
  },
  deletingRow: {
    opacity: ATTACHMENT_COLORS.DELETING_OPACITY,
  },
  nameColumn: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingRight: 12,
  },
  nameContent: {
    flex: 1,
    marginLeft: 12,
  },
  tableFilename: {
    fontSize: 14,
    fontWeight: '500',
    color: ATTACHMENT_COLORS.FILENAME_COLOR,
    marginBottom: 4,
  },
  // Table caption variants share identical box model for consistent row height
  tableCaption: {
    ...TABLE_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.CAPTION_COLOR,
  },
  tableCaptionInput: {
    ...TABLE_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.CAPTION_COLOR,
  },
  tableCaptionPlaceholder: {
    ...TABLE_CAPTION_BASE,
    fontSize: 13,
    fontStyle: 'italic',
    color: ATTACHMENT_COLORS.METADATA_COLOR,
  },
  dateColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  sizeColumn: {
    width: 100,
    paddingHorizontal: 8,
  },
  actionsColumn: {
    width: 120,
    paddingHorizontal: 8,
  },
  tableText: {
    fontSize: 14,
    color: ATTACHMENT_COLORS.CAPTION_COLOR,
  },

  // Error display
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 4,
  },
  errorText: {
    fontSize: 12,
    color: ATTACHMENT_COLORS.DELETE_BUTTON_COLOR,
    flex: 1,
  },
  retryText: {
    fontSize: 12,
    color: ATTACHMENT_COLORS.ACTION_BUTTON_COLOR,
    fontWeight: '500',
    marginLeft: 8,
  },
});
